import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {repaintView} from '../../../admin-core/utils/loading-state.util';
import {revealInScrollport} from '../../../admin-core/utils/reveal-in-scrollport.util';
import {lockPageScroll, unlockPageScroll} from '../../scroll-lock';
import {returnFocusToSearch} from '../../directives/return-search-focus.directive';

export type RangeKey =
  'Any Time' | 'Today' | 'Yesterday' | 'This week' | 'Last week' | 'This month' | 'Last month' |
  'MTD' | 'QTD' | 'YTD' | 'This FY' | 'Last FY' | 'Custom';

export interface DateRangeChange {
  /** null = no date filter ("Any time", only in optional mode) */
  key: RangeKey | null;
  from: Date | null;
  to: Date | null;
}

interface PresetGroup {
  label: string;
  items: RangeKey[];
}

interface CalDay {
  date: Date | null;
}

/**
 * Detailed period filter — mirrors the BA dashboard's DateRangeFilter:
 * preset rail (day / week / month / quarter & year / AU financial year)
 * plus a two-month custom range calendar.
 *
 * `optional` mode adds an "Any time" state (no filter applied) — used for
 * per-column date filters like the Accounts activity columns.
 */
@Component({
  selector: 'app-date-range-filter',
  standalone: true,
  templateUrl: './date-range-filter.component.html',
  styleUrls: ['./date-range-filter.component.css'],
  host: {'[class.drf-as-field]': "variant === 'field'"},
})
export class DateRangeFilterComponent implements OnInit, AfterViewChecked, OnDestroy {

  /** When true, starts unset ("Any time") and offers it as an option. */
  @Input() optional = false;

  /** Which edge of the trigger the popover aligns to. */
  @Input() align: 'left' | 'right' = 'right';

  /**
   * `field` draws the trigger as a filter-panel dropdown (console-v2's look for a
   * date filter inside `.f-field`): the panel's `.fdd-btn` fill and size, caret at
   * the right. Opt-in, so the listings' date pills keep their standalone look.
   */
  @Input() variant: 'pill' | 'field' = 'pill';

  /**
   * Starting selection, read once on init - a listing restoring the range it had when
   * the user left it (see list-state.util). `key` null means "Any time". A preset
   * re-resolves against today; 'Custom' reopens on the saved endpoints.
   */
  @Input() initial: { key: string | null; from?: Date | string | null; to?: Date | string | null } | null = null;

  @Output() rangeChange = new EventEmitter<DateRangeChange>();

  /**
   * The rail below the "Any time" / "All" head group, which the template renders
   * per mode: `optional` (every list filter) gets console-v2's un-labelled
   * "Any time" button, which CLEARS the filter; the dashboard, which must always
   * send a concrete span, gets the 'Any Time' preset instead (2022 → today).
   */
  presetGroups: PresetGroup[] = [
    {label: 'Day', items: ['Today', 'Yesterday']},
    {label: 'Week', items: ['This week', 'Last week']},
    {label: 'Month', items: ['This month', 'Last month', 'MTD']},
    {label: 'Quarter & Year', items: ['QTD', 'YTD']},
    {label: 'Financial Year (AU)', items: ['This FY', 'Last FY']}
  ];

  /**
   * Popover open state.
   *
   * A getter/setter pair rather than a plain field because on a phone the
   * popover is not a popover at all — it is a full-screen sheet (see the
   * `max-width: 640px` block in the stylesheet), so the page behind it has to
   * be frozen. Freezing also removes the root scrollbar, which is what was
   * holding the sheet ~16px short of the right screen edge.
   */
  private _open = false;

  get open(): boolean {
    return this._open;
  }

  set open(value: boolean) {
    const wasOpen = this._open;
    this._open = value;
    if (value && this.isPhoneSheet) {
      lockPageScroll('.pop', this);
    } else {
      unlockPageScroll(this);
    }
    // Every close — preset, Apply, ✕, outside click, Escape — hands the caret back
    // to the listing's search. The sheet may sit on <body>, so it counts as ours.
    if (wasOpen && !value) {
      returnFocusToSearch(this.host.nativeElement, this.portaled);
    }
  }

  /** Resolved after the popover renders — may flip from `align` to stay on screen. */
  effectiveAlign: 'left' | 'right' = 'right';
  /**
   * Extra horizontal nudge (px) keeping the popover inside the viewport when
   * NEITHER edge alignment fits, applied as a translateX. Flipping alone is not
   * enough below ~700px, where the popover is wider than the space either side
   * of its trigger.
   */
  popShift = 0;
  /**
   * False for the single frame between the popover entering the DOM and
   * `reposition()` measuring it, so it never paints at the pre-measurement
   * position. Never gated on measurement succeeding — see `reposition()`.
   */
  popPlaced = false;
  /** Breathing room kept between the popover and the viewport edge. */
  private static readonly GUTTER = 12;
  /** The sheet once it has been moved to `<body>` — see `ngAfterViewChecked()`. */
  private portaled: HTMLElement | null = null;
  key: RangeKey = 'This FY';
  /** false = "Any time" (optional mode only) */
  active = true;

  customFrom: Date | null = null;
  customTo: Date | null = null;

  /** First-of-month anchoring the LEFT calendar; right shows the next month. */
  calAnchor = (() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  })();

  dow = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.effectiveAlign = this.align;
    if (this.optional) {
      this.active = false;
    }
    this.applyInitial();
    // Defer the initial emit so it doesn't mutate parent-bound state during the
    // parent's first change-detection pass (avoids NG0100 on the dashboard loader).
    Promise.resolve().then(() => this.emit());
  }

  /**
   * Puts the picker on the `initial` selection, so its first emit announces that range
   * (and a listing restoring it sees no change) instead of "Any time".
   */
  private applyInitial(): void {
    const start = this.initial;
    if (!start?.key) {
      return;
    }
    this.key = start.key as RangeKey;
    this.active = true;
    if (this.key === 'Custom') {
      const from = start.from ? new Date(start.from) : null;
      const to = start.to ? new Date(start.to) : null;
      this.customFrom = from && !isNaN(from.getTime()) ? from : null;
      this.customTo = to && !isNaN(to.getTime()) ? to : null;
      if (this.customFrom) {
        this.calAnchor = new Date(this.customFrom.getFullYear(), this.customFrom.getMonth(), 1);
      }
    }
  }

  /* ── resolved range ──────────────────────────────────────── */

  get resolved(): { from: Date, to: Date } {
    return this.rangeToDates(this.key);
  }

  get buttonLabel(): string {
    const {from, to} = this.resolved;
    return `${this.fmtShort(from)} - ${this.fmtLong(to)}`;
  }

  get summaryLabel(): string {
    const {from, to} = this.resolved;
    return `${this.fmtFull(from)} - ${this.fmtFull(to)}`;
  }

  private rangeToDates(key: RangeKey): { from: Date, to: Date } {
    const now = new Date();
    const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const shift = (d: Date, days: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
    const startOfWeek = (d: Date) => shift(day(d), -((d.getDay() + 6) % 7)); // Monday
    switch (key) {
      case 'Any Time':
        // "All available data" — a fixed 1 Jan 2022 floor through today. The end
        // date is always the current date at request time (never hardcoded).
        return {from: new Date(2022, 0, 1), to: day(now)};
      case 'Today':
        return {from: day(now), to: day(now)};
      case 'Yesterday': {
        const y = shift(now, -1);
        return {from: y, to: y};
      }
      case 'This week':
        return {from: startOfWeek(now), to: shift(startOfWeek(now), 6)};
      case 'Last week': {
        const lw = startOfWeek(shift(now, -7));
        return {from: lw, to: shift(lw, 6)};
      }
      case 'This month':
        return {from: new Date(now.getFullYear(), now.getMonth(), 1), to: new Date(now.getFullYear(), now.getMonth() + 1, 0)};
      case 'Last month':
        return {from: new Date(now.getFullYear(), now.getMonth() - 1, 1), to: new Date(now.getFullYear(), now.getMonth(), 0)};
      case 'MTD':
        return {from: new Date(now.getFullYear(), now.getMonth(), 1), to: day(now)};
      case 'QTD': {
        const q = Math.floor(now.getMonth() / 3) * 3;
        return {from: new Date(now.getFullYear(), q, 1), to: day(now)};
      }
      case 'YTD':
        return {from: new Date(now.getFullYear(), 0, 1), to: day(now)};
      case 'This FY':
        return this.ausFY(now);
      case 'Last FY':
        return this.ausFY(new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()));
      case 'Custom':
        return {
          from: this.customFrom ?? new Date(now.getFullYear(), now.getMonth(), 1),
          to: this.customTo ?? day(now)
        };
    }
  }

  /** Australian financial year — 1 Jul → 30 Jun. */
  private ausFY(date: Date): { from: Date, to: Date } {
    const startYear = date.getMonth() >= 6 ? date.getFullYear() : date.getFullYear() - 1;
    return {from: new Date(startYear, 6, 1), to: new Date(startYear + 1, 5, 30)};
  }

  /* ── interaction ─────────────────────────────────────────── */

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    const willOpen = !this.open;
    // Only one filter popover may be open at a time, app-wide
    document.dispatchEvent(new Event('gc-close-popovers'));
    this.open = willOpen;
    if (willOpen) {
      this.schedulePlacement();
    }
  }

  /**
   * Place the popover once it is in the DOM and can be measured.
   *
   * Measuring is the whole point: the popover's width depends on its MODE — the
   * preset rail plus summary is ~510px, the rail plus the two-month calendar
   * ~678px. The previous implementation guessed a single `POP_WIDTH = 540`, which
   * covered the preset mode and missed Custom range by ~140px: opened from the
   * CREATED filter on a 1850px window the calendar hung ~110px off the right
   * edge with the second month unreachable, because neither the filter panel nor
   * the page scrolls horizontally. Vertical overflow is recoverable (the page
   * scrolls), horizontal overflow is not.
   */
  private schedulePlacement(): void {
    this.popPlaced = false;
    if (typeof requestAnimationFrame !== 'function') {
      this.popPlaced = true;   // non-browser (SSR/tests): never hide the popover
      return;
    }
    requestAnimationFrame(() => this.reposition());
  }

  /**
   * Prefer `align`; flip to the opposite edge when the preferred side would
   * overflow; nudge back inside when neither side fits. Reads the popover's real
   * width and the TRIGGER's untransformed rect, so it is idempotent — calling it
   * again never compounds an existing shift.
   */
  private reposition(): void {
    // Placed unconditionally, even if the element has gone (closed within the
    // frame) or cannot be measured: a popover left permanently invisible would be
    // a far worse failure than one a few pixels off.
    this.popPlaced = true;
    const pop = this.popEl();
    if (!pop) {
      return;
    }

    /* Phones: there is nothing to place. The sheet is `position: fixed` and
       spans the viewport, so every output of this pass is not just unnecessary
       but actively wrong — the `maxWidth` cap and the `translateX` nudge would
       shrink the sheet and slide it off-centre, and `revealInScrollport()`
       would scroll the (now frozen) page under it. Anything a previous
       desktop-width pass left behind is cleared here, so rotating a phone from
       landscape to portrait doesn't strand an inline width on the sheet. */
    if (this.isPhoneSheet) {
      pop.style.maxWidth = '';
      this.effectiveAlign = this.align;
      this.popShift = 0;
      repaintView(this.cdr);
      return;
    }
    const anchor = this.host.nativeElement.getBoundingClientRect();
    const viewport = document.documentElement.clientWidth;
    const gutter = DateRangeFilterComponent.GUTTER;
    // Sliding the popover back inside cannot help when it is WIDER than the
    // screen, so cap it first — `.cals` then wraps the second month underneath
    // the first rather than having it clipped. Set from clientWidth rather than a
    // CSS `100vw`, which counts the scrollbar and would leave the right edge
    // under it. Applied before measuring, so `width` below is the capped width.
    pop.style.maxWidth = Math.max(0, viewport - gutter * 2) + 'px';
    const width = pop.getBoundingClientRect().width;   // unaffected by translateX

    const leftEdgeIf = (side: 'left' | 'right') => side === 'left' ? anchor.left : anchor.right - width;
    const fits = (left: number) => left >= gutter && left + width <= viewport - gutter;

    let side = this.align;
    if (!fits(leftEdgeIf(side))) {
      const other: 'left' | 'right' = side === 'left' ? 'right' : 'left';
      if (fits(leftEdgeIf(other))) {
        side = other;
      }
    }
    this.effectiveAlign = side;

    // Right overflow is corrected first, then the left edge is guarded, so a
    // popover wider than the viewport itself ends up pinned to the left gutter
    // rather than pushed further off-screen.
    const left = leftEdgeIf(side);
    let shift = 0;
    if (left + width > viewport - gutter) {
      shift = (viewport - gutter) - (left + width);
    }
    if (left + shift < gutter) {
      shift = gutter - left;
    }
    this.popShift = Math.round(shift);
    // Horizontal overflow is now contained; vertical overflow is recoverable, so
    // it is corrected by moving the PAGE rather than the popover — the popover
    // stays where its CSS hangs it, under its trigger, at every viewport. Done
    // here, at the end of the placement pass, because the `maxWidth` cap above
    // can wrap `.cals` and change the popover's height. Idempotent: once it fits,
    // the required scroll is zero.
    revealInScrollport(pop, {gap: gutter});
    // This component is OnPush (Angular 22's default — see the notes in main.ts on
    // provideZoneChangeDetection), and the assignments above happen in a
    // requestAnimationFrame callback, so nothing has marked this view dirty. Without
    // an explicit repaint the popover keeps `visibility: hidden` from
    // `schedulePlacement()` and never appears at all.
    repaintView(this.cdr);
  }

  /** The popover is anchored to the trigger, so a resize can invalidate its placement. */
  @HostListener('window:resize')
  onViewportResize(): void {
    if (this.open) {
      this.reposition();
    }
  }

  /**
   * Phones: hoist the sheet to `<body>`.
   *
   * `position: fixed` resolves against the nearest TRANSFORMED or FILTERED
   * ancestor, not the viewport, and several pages that host a date filter have
   * one — inside those the sheet renders in a card-sized box instead of
   * full-screen. On `<body>` the viewport is guaranteed to be its containing
   * block. Same portal `app-date-picker` uses, for the same reason; Angular
   * still owns the node, so the `@if` teardown removes it wherever it sits.
   *
   * AfterViewChecked rather than a timer: the popover only exists after the
   * `@if` has rendered, and this runs on every pass so a resize that crosses the
   * breakpoint while the sheet is open is picked up too.
   */
  ngAfterViewChecked(): void {
    if (!this.open || !this.isPhoneSheet) {
      return;
    }
    const pop = this.popEl();
    if (pop && pop.parentElement !== document.body) {
      document.body.appendChild(pop);
      this.portaled = pop;
    }
  }

  ngOnDestroy(): void {
    // A route change can tear this down with the sheet still open. The lock is
    // global, so skipping this leaves the whole app unscrollable.
    if (this._open) {
      unlockPageScroll(this);
    }
  }

  /** True while the popover renders as the full-screen phone sheet. */
  private get isPhoneSheet(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 640px)').matches;
  }

  /** The popover element — inside the host normally, on `<body>` once portaled. */
  private popEl(): HTMLElement | null {
    return this.host.nativeElement.querySelector('.pop') as HTMLElement | null
      ?? (this.portaled?.isConnected ? this.portaled : null);
  }

  stop(event: MouseEvent): void {
    event.stopPropagation();
  }

  @HostListener('document:gc-close-popovers')
  @HostListener('document:click')
  closeOnOutside(): void {
    this.open = false;
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    this.open = false;
  }

  selectPreset(key: RangeKey): void {
    const wasCustom = this.key === 'Custom';
    this.key = key;
    this.active = true;
    if (key !== 'Custom') {
      this.open = false;
      this.emit();
      return;
    }
    // Entering (or re-entering) Custom range swaps the summary pane for the
    // two-month calendar and widens the popover by ~170px, so it has to be
    // placed again — placement computed at open time is stale the moment the
    // mode changes, which is exactly how the calendar ended up off-screen.
    if (!wasCustom) {
      this.schedulePlacement();
    }
  }

  /**
   * Back to "Any time" — no date filter (optional mode).
   *
   * The custom endpoints go with it. They are the one piece of picked state that outlives
   * `active`: leave them and re-entering "Custom range…" reopens on the span the user just
   * cleared, with its dates already highlighted and the Apply footer summarising them. `key`
   * deliberately stays as the last preset — nothing renders it while the filter is unset (the
   * template reads `active`), and it is what re-opening the rail would highlight if this ever
   * grew a "restore last range" affordance.
   */
  clearToAny(): void {
    this.active = false;
    this.open = false;
    this.customFrom = null;
    this.customTo = null;
    this.emit();
  }

  /** External reset hook (e.g. filter chips / Clear all). */
  reset(): void {
    if (this.optional) {
      this.clearToAny();
    }
  }

  apply(): void {
    if (this.customFrom && this.customTo) {
      this.active = true;
      this.open = false;
      this.emit();
    }
  }

  private emit(): void {
    if (this.optional && !this.active) {
      this.rangeChange.emit({key: null, from: null, to: null});
      return;
    }
    // "Any Time" resolves to the fixed 2022-01-01 → today span (see
    // rangeToDates), so it flows through the normal dated path — the request
    // always carries a concrete start/end, never a null range.
    const {from, to} = this.resolved;
    this.rangeChange.emit({key: this.key, from, to});
  }

  /* ── custom range calendar ───────────────────────────────── */

  get months(): Date[] {
    return [
      this.calAnchor,
      new Date(this.calAnchor.getFullYear(), this.calAnchor.getMonth() + 1, 1)
    ];
  }

  shiftMonths(delta: number): void {
    this.calAnchor = new Date(this.calAnchor.getFullYear(), this.calAnchor.getMonth() + delta, 1);
  }

  weeks(month: Date): CalDay[][] {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const lead = (first.getDay() + 6) % 7; // Monday-first offset
    const cells: CalDay[] = [];
    for (let i = 0; i < lead; i++) {
      cells.push({date: null});
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({date: new Date(month.getFullYear(), month.getMonth(), d)});
    }
    while (cells.length % 7 !== 0) {
      cells.push({date: null});
    }
    const rows: CalDay[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      rows.push(cells.slice(i, i + 7));
    }
    return rows;
  }

  pick(date: Date): void {
    if (!this.customFrom || (this.customFrom && this.customTo)) {
      this.customFrom = date;
      this.customTo = null;
      return;
    }
    if (date.getTime() < this.customFrom.getTime()) {
      this.customTo = this.customFrom;
      this.customFrom = date;
    } else {
      this.customTo = date;
    }
  }

  isEndpoint(date: Date): boolean {
    return this.sameDay(date, this.customFrom) || this.sameDay(date, this.customTo);
  }

  inRange(date: Date): boolean {
    if (!this.customFrom || !this.customTo) {
      return false;
    }
    const t = date.getTime();
    return t > this.customFrom.getTime() && t < this.customTo.getTime();
  }

  private sameDay(a: Date, b: Date | null): boolean {
    return !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  /* ── formatting ──────────────────────────────────────────── */

  fmtShort(d: Date): string {
    return d.toLocaleDateString('en-AU', {day: 'numeric', month: 'short'});
  }

  fmtLong(d: Date): string {
    return d.toLocaleDateString('en-AU', {day: 'numeric', month: 'short', year: 'numeric'});
  }

  fmtFull(d: Date): string {
    return d.toLocaleDateString('en-AU', {weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'});
  }

  fmtMonth(d: Date): string {
    return d.toLocaleDateString('en-AU', {month: 'long', year: 'numeric'});
  }

  get customSummary(): string {
    if (this.customFrom && this.customTo) {
      return `${this.fmtLong(this.customFrom)} - ${this.fmtLong(this.customTo)}`;
    }
    if (this.customFrom) {
      return `${this.fmtLong(this.customFrom)} - select an end date`;
    }
    return 'Select a start and end date';
  }
}
